// src/hooks/api/api.ts

import ErrorReporter from 'src/monitoring/errorReporter';
import { OpenAPI } from '../../api/generated';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useRef } from 'react';
import type { RefetchOptions } from '@tanstack/react-query';
import { EMPTY_HEADERS } from 'src/modules/shared/constants/api/api.constants';
import { AuthTokenUnavailableError } from 'src/modules/shared/errors/auth/auth-token-unavailable.error';
import {
  getAuthErrorCode,
  isRetryableAuthError,
} from 'src/modules/shared/utils/auth/auth-error.util';
import { resolveSupportModeHeaders } from 'src/modules/shared/utils/http/support-mode-headers.util';

export interface ApiResponse {
  loading: boolean;
  error: Error | null;
  refetch: (options?: RefetchOptions) => Promise<unknown>;
}

export const useSetApiToken = () => {
  const { getAccessTokenSilently } = useAuth0();

  const getToken = async () => {
    try {
      const token = await getAccessTokenSilently();
      return token;
    } catch (e) {
      ErrorReporter.sendException(e);
      throw new AuthTokenUnavailableError('Unable to retrieve access token.', {
        cause: e,
        code: getAuthErrorCode(e),
        retryable: isRetryableAuthError(e),
      });
    }
  };

  OpenAPI.TOKEN = getToken;
  OpenAPI.HEADERS = () => {
    const headers = resolveSupportModeHeaders();
    return Promise.resolve(
      Object.keys(headers).length > 0 ? headers : EMPTY_HEADERS
    );
  };
};

export const useMemoizedArrayData = <TDataResult>(
  dataResult: TDataResult[] | undefined
): TDataResult[] => {
  const previousData = useRef<TDataResult[]>([]);

  const memoizedData = useMemo(() => {
    const newData = dataResult ?? [];

    // If array length changed, it's definitely different
    if (newData.length !== previousData.current.length) {
      previousData.current = newData;
      return newData;
    }

    // If same length, check if all items are referentially equal (shallow comparison)
    const hasChanged = newData.some(
      (item, index) => item !== previousData.current[index]
    );

    if (hasChanged) {
      previousData.current = newData;
      return newData;
    }

    // Content is the same, return the previous reference to maintain stability
    return previousData.current;
  }, [dataResult]);

  return memoizedData;
};
